"use client";

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import Image from 'next/image';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useVideoManager } from '../hooks/use-video-manager';

export interface VideoPlayerProps {
  analyticsCategory?: string;
  className?: string;
  description: string;
  lazy?: boolean;
  onPause?: () => void;
  onPlay?: () => void;
  src: string;
  thumbnail: string;
  title: string;
  videoId: string;
}

type VideoState = 'error' | 'loading' | 'ready';

const IndividualVideoPlayer = memo(
  ({
    analyticsCategory = 'page',
    className = '',
    description,
    lazy = false,
    onPause,
    onPlay,
    src,
    thumbnail,
    title,
    videoId,
  }: VideoPlayerProps) => {
      const [isLoaded, setIsLoaded] = useState(!lazy);
  const [videoState, setVideoState] = useState<VideoState>('loading');
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

    const { trackEvent } = useInteractionTracker();
    const { getCurrentlyPlaying, pauseVideo, playVideo, registerVideo } = useVideoManager();

    // Register video element with the video manager
    useEffect(() => {
      if (videoRef.current) {
        const cleanup = registerVideo(videoId, videoRef.current);
        return cleanup;
      }
    }, [videoId, registerVideo, isLoaded]);

    // Intersection observer for lazy loading
    const { inView, ref: inViewRef } = useInView({
      threshold: 0.1,
      triggerOnce: true,
    });

    // Handle lazy loading
    useEffect(() => {
      if (lazy && inView && !isLoaded) {
        setIsLoaded(true);
      }
    }, [inView, isLoaded, lazy]);

    // Handle fullscreen changes
    useEffect(() => {
      const handleFullscreenChange = () => {
        const isVideoFullscreen = document.fullscreenElement === videoRef.current;
        setIsFullscreen(isVideoFullscreen);
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('msfullscreenchange', handleFullscreenChange);

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      };
    }, []);

    // Video event handlers
    const handleVideoLoadStart = useCallback(() => {
      setVideoState('loading');
    }, []);

    const handleVideoCanPlay = useCallback(() => {
      setVideoState('ready');
    }, []);

    const handleVideoError = useCallback(() => {
      setVideoState('error');
      void trackEvent({ action: 'error-video', category: analyticsCategory, label: `video-${videoId}` });
    }, [analyticsCategory, trackEvent, videoId]);

    const handleVideoPlay = useCallback(() => {
      setShowThumbnail(false);
      playVideo(videoId);
      onPlay?.();
      void trackEvent({ action: 'play-video', category: analyticsCategory, label: `video-${videoId}` });
    }, [analyticsCategory, onPlay, playVideo, trackEvent, videoId]);

    const handleVideoPause = useCallback(() => {
      pauseVideo(videoId);
      onPause?.();
      void trackEvent({ action: 'pause-video', category: analyticsCategory, label: `video-${videoId}` });
    }, [analyticsCategory, onPause, pauseVideo, trackEvent, videoId]);

    const handleVideoEnded = useCallback(() => {
      setShowThumbnail(true);
      void trackEvent({ action: 'end-video', category: analyticsCategory, label: `video-${videoId}` });
    }, [analyticsCategory, trackEvent, videoId]);

    // Pause video when another video starts playing
    useEffect(() => {
      const currentlyPlaying = getCurrentlyPlaying();
      if (
        currentlyPlaying &&
        currentlyPlaying !== videoId &&
        videoRef.current
      ) {
        videoRef.current.pause();
        setShowThumbnail(true);
      }
    }, [getCurrentlyPlaying, videoId]);

    // Handle thumbnail click
    const handleThumbnailClick = useCallback(() => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    }, []);

    return (
      <div className={`flex justify-center ${className}`} ref={containerRef}>
        <div
          className="bg-gradient-to-br from-surface-tertiary to-surface-interactive rounded-xl overflow-hidden border border-glass shadow-lg"
          style={{ maxWidth: '360px', width: '100%' }}
        >
          <div
            className="relative bg-surface-secondary"
            style={{ aspectRatio: '9/16' }}
          >
            {isLoaded ? (
              <>
                {/* Hidden description for screen readers */}
                <div className="sr-only" id={`video-desc-${videoId}`}>
                  {description}. Use space or enter to play video.
                </div>

                <video
                  aria-describedby={`video-desc-${videoId}`}
                  aria-label={`${title} - ${description}`}
                  className={`w-full h-full ${isFullscreen ? 'object-contain bg-black' : 'object-cover'}`}
                  controls
                  controlsList="nodownload"
                  data-video-id={videoId}
                  onCanPlay={handleVideoCanPlay}
                  onEnded={handleVideoEnded}
                  onError={handleVideoError}
                  onLoadStart={handleVideoLoadStart}
                  onPause={handleVideoPause}
                  onPlay={handleVideoPlay}
                  playsInline
                  preload="metadata"
                  ref={videoRef}
                  src={src}
                  style={{
                    display: showThumbnail ? 'none' : 'block',
                    touchAction: 'manipulation',
                    transform: 'translateZ(0)',
                  }}
                  webkit-playsinline="true"
                />

                {videoState === 'loading' && !showThumbnail && (
                  <div className="absolute inset-0 flex items-center justify-center bg-surface-secondary">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-secondary"></div>
                  </div>
                )}

                {videoState === 'error' && !showThumbnail && (
                  <div className="absolute inset-0 flex items-center justify-center bg-surface-secondary">
                    <div className="text-semantic-error text-center">
                      <svg
                        className="w-12 h-12 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                      <p className="text-sm">Video unavailable</p>
                    </div>
                  </div>
                )}

                {showThumbnail && (
                  <button
                    aria-label={`Play video: ${title}`}
                    className="absolute inset-0 group cursor-pointer focus-ring-primary"
                    onClick={handleThumbnailClick}
                    type="button"
                  >
                    <Image
                      alt={`${title} thumbnail`}
                      className="w-full h-full object-cover"
                      height={640}
                      priority={!lazy}
                      src={thumbnail}
                      width={360}
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                  </button>
                )}
              </>
            ) : (
              // Placeholder for lazy loading
              <div className="w-full h-full bg-surface-interactive flex items-center justify-center">
                <div className="text-text-disabled">
                  <svg
                    className="w-16 h-16 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Intersection observer ref for lazy loading */}
            {lazy && <div ref={inViewRef} />}
          </div>
        </div>
      </div>
    );
  }
);

IndividualVideoPlayer.displayName = 'IndividualVideoPlayer';

export default IndividualVideoPlayer;
